/**
 * Shared envelope attachment resolution for dispute wizard Steps 5–8.
 */

import React from 'react';
import { FileCheck } from 'lucide-react';
import {
  DOCUMENTS_BACKEND_URL,
  isServerSavedDocument,
  isIdentityTheftReportAttachment,
  preparedDocumentToPackageAttachment,
  resolvePreparedDocumentUrl,
} from './supportingDocumentApi';

export const DATA_BREACH_PROOF_FILENAME = 'Data Breach.png';
/** Bust CDN/browser cache when the bundled dispute asset is updated. */
export const DATA_BREACH_PROOF_ASSET_VERSION = '1';

export function getDisputeAssetsBaseUrl() {
  return `${DOCUMENTS_BACKEND_URL}/api/dispute-assets`;
}

/** Canonical public URL for the bundled system default (encoded path + cache buster). */
export function getSystemDefaultDataBreachProofAssetUrl() {
  const encoded = encodeURIComponent(DATA_BREACH_PROOF_FILENAME);
  return `${getDisputeAssetsBaseUrl()}/${encoded}?v=${DATA_BREACH_PROOF_ASSET_VERSION}`;
}

export function buildSystemDefaultDataBreachProof() {
  const serverUrl = getSystemDefaultDataBreachProofAssetUrl();
  return {
    category: 'data_breach_proof',
    type: 'data_breach_proof',
    source: 'system_default',
    filename: DATA_BREACH_PROOF_FILENAME,
    title: 'Data Breach Proof',
    label: 'Data Breach Proof',
    server_url: serverUrl,
    file_url: serverUrl,
    cropped_url: serverUrl,
    preview_url: serverUrl,
    thumbnail_url: serverUrl,
    is_server_saved: true,
    included: true,
    cropped: true,
    auto_attached: true,
    attached: true,
  };
}

const NON_DATA_BREACH_METHOD_TOKENS = [
  'isolated',
  'isolated_method',
  'meth_isolated',
  'structural',
  'structural_method',
  'recovery',
  'debt_validation',
  'metro2',
  'metro_2',
  'factual_inaccuracy',
  'meth_factual_inaccuracy',
];

function letterHasExplicitDataBreachSignal(values) {
  return values.some(
    (v) =>
      v === 'data_breach' ||
      v === 'data_breach_method' ||
      v === 'data breach' ||
      v === 'meth_data_breach' ||
      v.includes('data_breach') ||
      v.includes('data_breach_method') ||
      (v.includes('data') && v.includes('breach'))
  );
}

/** Strict Data Breach letter/envelope detection (not generic "breach" substring). */
export function isDataBreachLetter(letter) {
  if (!letter) return false;
  if (letter.envelope_type === 'data_breach') return true;

  const values = [
    letter.method,
    letter.method_used,
    letter.method_key,
    letter.method_name,
    letter.family,
    letter.envelope_type,
    letter.strategy,
    letter.strategy_label,
    letter.package_method,
    letter.package_subtype,
    letter.prompt_family,
    letter.category,
    letter.package_name,
    letter.package_label,
    letter.letter_type,
  ]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase().trim());

  if (!letterHasExplicitDataBreachSignal(values)) {
    return false;
  }

  const hasNonDataBreachMethod = values.some((v) =>
    NON_DATA_BREACH_METHOD_TOKENS.some((token) => v === token || v.includes(token))
  );
  if (hasNonDataBreachMethod && !values.some((v) => v.includes('data_breach') || v.includes('data breach'))) {
    return false;
  }

  return true;
}

export function isDataBreachEnvelope(envelope) {
  if (!envelope) return false;
  if (envelope.envelope_type === 'data_breach') return true;
  return isDataBreachLetter(envelope);
}

/** Normalize bureau names for stable envelope/letter dedupe keys. */
export function normalizeDisputeBureau(bureau) {
  const raw = String(bureau || '').trim().toLowerCase();
  const aliases = {
    eq: 'equifax',
    ex: 'experian',
    tu: 'transunion',
    equifax: 'equifax',
    experian: 'experian',
    transunion: 'transunion',
    'trans union': 'transunion',
  };
  return aliases[raw] || raw;
}

/** Method/family token for envelope dedupe (data breach vs other dispute methods). */
export function getDisputeEnvelopeMethodFamily(envelopeOrLetter) {
  const nested = envelopeOrLetter?.letters?.[0];
  const values = [
    envelopeOrLetter?.method,
    envelopeOrLetter?.envelope_type,
    nested?.method_used,
    nested?.method,
    nested?.package_method,
    nested?.package_subtype,
    nested?.prompt_family,
    nested?.family,
    nested?.strategy,
    envelopeOrLetter?.method_used,
    envelopeOrLetter?.package_method,
    envelopeOrLetter?.package_subtype,
    envelopeOrLetter?.prompt_family,
  ]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase().trim());

  if (values.some((v) => letterHasExplicitDataBreachSignal([v]))) {
    return 'data_breach';
  }
  return values[0] || 'unknown';
}

function buildDataBreachBureauKey({ packageId, bureau, methodFamily }) {
  return `${packageId || ''}|${normalizeDisputeBureau(bureau)}|${methodFamily}`;
}

/**
 * Remove exact duplicate Data Breach letters (same package, bureau, method family).
 * Non-data-breach letters are never deduped — mixed-method packages stay intact.
 */
export function dedupeDataBreachLetters(letters = [], { packageId = '' } = {}) {
  const result = [];
  const removed = [];
  const seenDataBreachBureau = new Set();

  (letters || []).forEach((letter) => {
    if (!isDataBreachLetter(letter)) {
      result.push(letter);
      return;
    }

    const bureauKey = buildDataBreachBureauKey({
      packageId,
      bureau: letter.bureau,
      methodFamily: getDisputeEnvelopeMethodFamily(letter),
    });

    if (seenDataBreachBureau.has(bureauKey)) {
      removed.push({
        bureau: normalizeDisputeBureau(letter.bureau),
        letter_id: letter.letter_id || letter.id || '',
        dedupe_key: bureauKey,
      });
      return;
    }

    seenDataBreachBureau.add(bureauKey);
    result.push(letter);
  });

  return { letters: result, removedCount: removed.length, removed };
}

/**
 * Remove duplicate Data Breach envelopes before quote/send (one per bureau per package).
 */
export function dedupeDataBreachEnvelopes(envelopes = [], { packageId = '' } = {}) {
  const result = [];
  const removed = [];
  const seenDataBreachBureau = new Set();

  (envelopes || []).forEach((envelope) => {
    if (!isDataBreachEnvelope(envelope)) {
      result.push(envelope);
      return;
    }

    const nested = envelope.letters?.[0];
    const bureauKey = buildDataBreachBureauKey({
      packageId: packageId || envelope.package_id,
      bureau: envelope.bureau || nested?.bureau,
      methodFamily: getDisputeEnvelopeMethodFamily(envelope),
    });

    if (seenDataBreachBureau.has(bureauKey)) {
      removed.push({
        envelope_id: envelope.envelope_id,
        bureau: normalizeDisputeBureau(envelope.bureau || nested?.bureau),
        letter_id:
          envelope.letter_id ||
          nested?.letter_id ||
          nested?.id ||
          envelope.envelope_id ||
          '',
        dedupe_key: bureauKey,
      });
      return;
    }

    seenDataBreachBureau.add(bureauKey);
    result.push(envelope);
  });

  return { envelopes: result, removedCount: removed.length, removed };
}

export function findUserDataBreachProofInLibrary(items) {
  if (!Array.isArray(items) || !items.length) return null;

  const score = (doc) => {
    if (doc.status === 'approved' || doc.status === 'verified') return 4;
    if (doc.is_attachment_ready) return 3;
    if (doc.status === 'cropped_draft') return 2;
    return 1;
  };

  const matches = items.filter((doc) => {
    const tags = doc.tags || [];
    const notes = (doc.notes || '').toLowerCase();
    const title = (doc.title || '').toLowerCase();
    if (tags.includes('data_breach_proof')) return true;
    if (notes.includes('data_breach_proof')) return true;
    if (title.includes('data breach')) return true;
    return false;
  });

  if (!matches.length) return null;

  return [...matches].sort((a, b) => {
    const diff = score(b) - score(a);
    if (diff !== 0) return diff;
    return (
      new Date(b.updated_at || b.created_at || 0).getTime() -
      new Date(a.updated_at || a.created_at || 0).getTime()
    );
  })[0];
}

export function resolveDataBreachProofFromLibrary(libraryItems) {
  const userDoc = findUserDataBreachProofInLibrary(libraryItems);
  if (userDoc) {
    return preparedDocumentToPackageAttachment(userDoc, 'data_breach_proof');
  }
  return buildSystemDefaultDataBreachProof();
}

/** Rewrite system-default proof URLs to the frontend API base (avoids stale/wrong backend host). */
export function normalizeDataBreachProof(proof) {
  if (!proof) return buildSystemDefaultDataBreachProof();

  const source = String(proof.source || '').toLowerCase();
  if (
    (source === 'existing_document' || source === 'backend_storage' || proof.document_id) &&
    isServerSavedDocument(proof)
  ) {
    return proof;
  }

  if (source === 'system_default' || source === 'backend_auto') {
    const assetUrl = getSystemDefaultDataBreachProofAssetUrl();
    return {
      ...buildSystemDefaultDataBreachProof(),
      ...proof,
      category: 'data_breach_proof',
      type: 'data_breach_proof',
      source: 'system_default',
      filename: DATA_BREACH_PROOF_FILENAME,
      title: proof.title || 'Data Breach Proof',
      server_url: assetUrl,
      file_url: assetUrl,
      cropped_url: assetUrl,
      preview_url: assetUrl,
      thumbnail_url: assetUrl,
      is_server_saved: true,
      auto_attached: true,
      included: true,
      attached: true,
    };
  }

  if (isServerSavedDocument(proof)) return proof;
  return buildSystemDefaultDataBreachProof();
}

export function resolveEffectiveDataBreachProof(proof) {
  if (!proof) return buildSystemDefaultDataBreachProof();
  if (proof.source === 'system_default' || proof.source === 'backend_auto') {
    return normalizeDataBreachProof(proof);
  }
  if (isResolvableDataBreachProof(proof)) return proof;
  return buildSystemDefaultDataBreachProof();
}

export function isResolvableDataBreachProof(proof) {
  if (!proof) return false;
  if (proof.source === 'system_default') return Boolean(proof.server_url || proof.file_url);
  return isServerSavedDocument(proof);
}

export function getAttachmentDisplayUrl(attachment) {
  if (!attachment) return null;

  if (attachment.source === 'system_default') {
    const category = String(attachment.category || attachment.type || '').toLowerCase();
    if (category === 'data_breach_proof') {
      return getSystemDefaultDataBreachProofAssetUrl();
    }
  }

  const url =
    attachment.server_url ||
    attachment.cropped_url ||
    attachment.preview_url ||
    attachment.file_url ||
    attachment.url;
  if (!url || (typeof url === 'string' && url.startsWith('blob:'))) return null;
  if (typeof url === 'string' && url.startsWith('/api')) {
    return `${DOCUMENTS_BACKEND_URL}${url}`;
  }
  return url;
}

/** Compact attachment preview with graceful fallback when image fails to load. */
export function AttachmentPreviewImage({
  attachment,
  alt = 'Attachment preview',
  className = 'w-full h-full object-contain',
  iconSize = 20,
}) {
  const category = String(attachment?.category || attachment?.type || '').toLowerCase();
  const isSystemDataBreach =
    category === 'data_breach_proof' &&
    (attachment?.source === 'system_default' || attachment?.source === 'backend_auto');

  const src = getAttachmentDisplayUrl(attachment);
  const fallbackSrc = isSystemDataBreach ? getSystemDefaultDataBreachProofAssetUrl() : null;
  const [displaySrc, setDisplaySrc] = React.useState(src);
  const [showFallback, setShowFallback] = React.useState(false);

  React.useEffect(() => {
    setDisplaySrc(getAttachmentDisplayUrl(attachment));
    setShowFallback(false);
  }, [attachment]);

  const activeSrc = showFallback && fallbackSrc ? fallbackSrc : displaySrc;

  if (!activeSrc) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-center px-1">
        <FileCheck size={iconSize} className="text-emerald-600 dark:text-emerald-400 mb-1" />
        <span className="text-[10px] leading-tight text-stone-500 dark:text-[#93AE9F]">
          Preview unavailable
        </span>
      </div>
    );
  }

  return (
    <img
      src={activeSrc}
      alt={alt}
      className={className}
      onError={() => {
        if (!showFallback && fallbackSrc && activeSrc !== fallbackSrc) {
          setShowFallback(true);
          return;
        }
        setDisplaySrc(null);
      }}
    />
  );
}

export const GROUPED_IMAGE_ATTACHMENT_TYPES = new Set([
  'proof_of_address',
  'government_id',
  'ssn_card',
  'ssn_support',
  'data_breach_proof',
]);

export const COMPACT_PROOF_ATTACHMENT_TYPES = new Set([
  'proof_of_address',
  'government_id',
  'ssn_card',
  'ssn_support',
  'data_breach_proof',
]);

export const RENDER_MODE_COMPACT_PROOF = 'compact_proof';
export const RENDER_MODE_FULL_PAGE = 'full_page';
export const RENDER_MODE_PDF_FULL_PAGE = 'pdf_full_page';
export const RENDER_MODE_IMAGE_GROUPED = 'image_grouped';

/** True when attachment is a PDF document (full-page append, preserve all pages). */
export function attachmentIsPdf(attachment) {
  if (!attachment) return false;
  const mime = String(
    attachment.content_type || attachment.media_type || attachment.mime_type || ''
  ).toLowerCase();
  if (mime === 'application/pdf') return true;
  if (attachment.is_from_pdf && attachment.source_file_type === 'pdf' && !attachment.cropped) {
    return true;
  }
  const name = String(attachment.filename || attachment.title || attachment.name || '').toLowerCase();
  const url = String(getAttachmentDisplayUrl(attachment) || '').toLowerCase();
  return name.endsWith('.pdf') || /\.pdf(?:\?|$)/.test(url);
}

/**
 * Resolve explicit printable render mode from attachment metadata and type.
 * Do not infer full-page rendering from image dimensions alone.
 */
export function resolveAttachmentRenderMode(attachment) {
  if (!attachment || typeof attachment !== 'object') return RENDER_MODE_IMAGE_GROUPED;

  const explicit = String(attachment.render_mode || '').toLowerCase();
  if (
    explicit === RENDER_MODE_COMPACT_PROOF ||
    explicit === RENDER_MODE_FULL_PAGE ||
    explicit === RENDER_MODE_PDF_FULL_PAGE ||
    explicit === RENDER_MODE_IMAGE_GROUPED
  ) {
    return explicit;
  }

  const type = normalizeAttachmentType(attachment);
  if (type === 'identity_theft_report') return RENDER_MODE_FULL_PAGE;
  if (COMPACT_PROOF_ATTACHMENT_TYPES.has(type)) return RENDER_MODE_COMPACT_PROOF;

  if (
    !COMPACT_PROOF_ATTACHMENT_TYPES.has(type) &&
    (attachment.full_page === true || attachment.is_full_page === true)
  ) {
    return RENDER_MODE_FULL_PAGE;
  }

  if (attachmentIsPdf(attachment)) return RENDER_MODE_PDF_FULL_PAGE;
  return RENDER_MODE_IMAGE_GROUPED;
}

/**
 * Classify attachment for printable / PostGrid rendering.
 * - compact_proof: identity packet items on one compact page
 * - full_page: standalone full-page image documents (e.g. Identity Theft Report)
 * - pdf_full_page: multi-page PDF append after grouped section
 * - image_grouped: generic supporting documents under APPENDED ATTACHMENTS
 */
export function classifyAttachmentRenderMode(attachment) {
  return resolveAttachmentRenderMode(attachment);
}

export function attachmentIsFullPage(attachment) {
  const mode = resolveAttachmentRenderMode(attachment);
  return mode === RENDER_MODE_FULL_PAGE || mode === RENDER_MODE_PDF_FULL_PAGE;
}

function buildPrintableRenderMetadata(type, attachment = {}) {
  const normalized = normalizeAttachmentType({ ...attachment, type });
  if (normalized === 'identity_theft_report') {
    return {
      render_mode: RENDER_MODE_FULL_PAGE,
      full_page: true,
      is_full_page: true,
      page_break_before: true,
    };
  }
  if (COMPACT_PROOF_ATTACHMENT_TYPES.has(normalized)) {
    return {
      render_mode: RENDER_MODE_COMPACT_PROOF,
      full_page: false,
      is_full_page: false,
      page_break_before: false,
    };
  }
  if (attachmentIsPdf(attachment)) {
    return {
      render_mode: RENDER_MODE_PDF_FULL_PAGE,
      full_page: true,
      is_full_page: true,
      page_break_before: true,
    };
  }
  if (attachment.full_page === true || attachment.is_full_page === true) {
    return {
      render_mode: RENDER_MODE_FULL_PAGE,
      full_page: true,
      is_full_page: true,
      page_break_before: true,
    };
  }
  return {
    render_mode: RENDER_MODE_IMAGE_GROUPED,
    full_page: false,
    is_full_page: false,
    page_break_before: false,
  };
}

/** Serialize attachments for printable / PostGrid PDF API payloads. */
export function buildPdfApiAttachments({
  letter,
  packageIdentitySupport = {},
  dataBreachProof = null,
  perLetterAttachments = {},
  packageSharedAttachments = [],
  letterId,
  ssnApprovals = {},
}) {
  return buildEnvelopePrintableAttachments({
    letter,
    packageIdentitySupport,
    dataBreachProof,
    perLetterAttachments,
    packageSharedAttachments,
    letterId,
    ssnApprovals,
  }).map((att) => ({
    type: att.type,
    category: att.category || att.type,
    document_id: att.document_id || att.id || undefined,
    filename: att.filename || att.title || att.label,
    title: att.title || att.label,
    source: att.source,
    server_url: getAttachmentDisplayUrl(att),
    file_url: getAttachmentDisplayUrl(att),
    cropped_url: getAttachmentDisplayUrl(att),
    preview_url: getAttachmentDisplayUrl(att),
    is_server_saved: Boolean(att.is_server_saved || att.source === 'system_default'),
    render_mode: att.render_mode || resolveAttachmentRenderMode(att),
    is_full_page: attachmentIsFullPage(att),
    full_page: attachmentIsFullPage(att),
    page_break_before: Boolean(att.page_break_before),
    crop_metadata: att.crop_metadata || undefined,
    shared: Boolean(att.shared || att.apply_to_all_envelopes || att.scope === 'package'),
    apply_to_all_envelopes: Boolean(att.apply_to_all_envelopes || att.shared || att.scope === 'package'),
    scope: att.scope || (att.shared || att.apply_to_all_envelopes ? 'package' : 'envelope'),
  }));
}

const ATTACHMENT_TYPE_ORDER = [
  'proof_of_address',
  'government_id',
  'ssn_card',
  'ssn_support',
  'data_breach_proof',
  'identity_theft_report',
];

function normalizeAttachmentType(att) {
  const raw = String(att.type || att.category || att.document_type || '').toLowerCase();
  if (raw === 'ssn_support') return 'ssn_card';
  if (raw === 'identity_verification') return 'government_id';
  return raw;
}

/** Deduplicate resolved attachments by URL, id, file_id, document_id, name, or filename. */
export function dedupeResolvedAttachments(attachments = []) {
  const deduped = [];
  const seenKeys = new Set();

  (attachments || []).forEach((att) => {
    if (!att) return;
    const url = getAttachmentDisplayUrl(att);
    const keyParts = [
      url ? `url:${url}` : null,
      att.document_id ? `document_id:${att.document_id}` : null,
      att.file_id ? `file_id:${att.file_id}` : null,
      att.id ? `id:${att.id}` : null,
      att.name ? `name:${att.name}` : null,
      att.filename ? `filename:${att.filename}` : null,
      att.title ? `title:${att.title}` : null,
    ].filter(Boolean);

    const dedupeKey = keyParts[0] || keyParts.join('|') || `type:${normalizeAttachmentType(att)}`;
    if (seenKeys.has(dedupeKey)) return;
    if (!url) return;
    seenKeys.add(dedupeKey);
    deduped.push(att);
  });

  return deduped;
}

export const ATTACHMENT_LABEL_MAP = {
  proof_of_address: 'Proof of Address',
  government_id: 'Government ID',
  ssn_card: 'SSN Card',
  data_breach_proof: 'Data Breach Proof',
  identity_theft_report: 'Identity Theft Report',
};

/** Default scope for Step 5 supporting documents attached to the full dispute package. */
export const PACKAGE_WIDE_ATTACHMENT_SCOPE = {
  shared: true,
  apply_to_all_envelopes: true,
  scope: 'package',
};

export function withPackageWideScope(attachment, source = 'package_shared') {
  if (!attachment) return attachment;
  return {
    ...attachment,
    ...PACKAGE_WIDE_ATTACHMENT_SCOPE,
    source: attachment.source || source,
  };
}

/** True when SSN attachment is approved for the entire dispute package (not per-bureau). */
export function isSsnApprovedPackageWide(ssnApprovals = {}) {
  return Object.values(ssnApprovals).some(Boolean);
}

/** Build per-letter ssnApprovals map with the same package-wide value for every envelope. */
export function buildPackageWideSsnApprovals(letterIds = [], approved = false) {
  const next = {};
  (letterIds || []).forEach((id) => {
    next[id] = Boolean(approved);
  });
  return next;
}

/** Normalize persisted approvals so one bureau flag applies package-wide. */
export function normalizeSsnApprovalsForPackage(ssnApprovals = {}, letterIds = []) {
  return buildPackageWideSsnApprovals(letterIds, isSsnApprovedPackageWide(ssnApprovals));
}

/** True when an attachment applies to every envelope in the dispute package. */
export function attachmentIsPackageWide(attachment) {
  if (!attachment) return false;
  if (attachment.scope === 'package') return true;
  if (attachment.apply_to_all_envelopes === true || attachment.shared === true) return true;
  const src = String(attachment.source || '');
  return src === 'package_level' || src === 'package_shared';
}

/** Clean display label — never raw filenames for known evidence types. */
export function getAttachmentDisplayLabel(attachment) {
  const type = normalizeAttachmentType(attachment);
  if (ATTACHMENT_LABEL_MAP[type]) return ATTACHMENT_LABEL_MAP[type];
  const title = (attachment?.title || attachment?.label || '').trim();
  if (title && !looksLikeRawFilename(title)) return title.replace(/\s*[-—]\s*Auto\s*Attached/gi, '').trim();
  return 'Supporting Document';
}

function looksLikeRawFilename(name) {
  const low = String(name).toLowerCase();
  if (/screenshot_\d+/.test(low)) return true;
  if (/^\d{10,}/.test(low)) return true;
  if (/\.(jpg|jpeg|png|webp|heic|pdf)$/i.test(low) && low.includes('_')) return true;
  return false;
}

export const APPENDED_ATTACHMENT_CSS = `
.appended-attachments-page { page-break-before: always; break-before: page; padding: 0.18in 0.75in 0.18in; box-sizing: border-box; }
.appended-attachments-page.compact-proof-page { min-height: 0; padding-bottom: 0.15in; }
.appended-attachments-title { text-align: center; font-weight: 700; font-size: 12pt; margin: 0 0 12px; letter-spacing: 0.08em; text-transform: uppercase; }
.attachment-page-full { page-break-before: always; break-before: page; margin: 0; padding: 0.15in 0.2in 0.15in; width: 100%; box-sizing: border-box; }
.attachment-image-full { display: block; object-fit: contain; margin: 0 auto; max-width: 100%; max-height: 10.3in; width: auto; height: auto; }
.attachment-item { break-inside: avoid; page-break-inside: avoid; margin-bottom: 10px; overflow: hidden; }
.attachment-item.compact-proof-item:last-child { margin-bottom: 0; }
.attachment-label { font-weight: 700; font-size: 11pt; margin: 0 0 4px; }
.attachment-image { display: block; object-fit: contain; margin: 0 auto; max-width: 100%; max-height: 2.1in; height: auto; width: auto; overflow: hidden; }
.attachment-image.proof_of_address { max-width: 65%; }
.attachment-image.government_id { max-width: 60%; }
.attachment-image.data_breach_proof { max-width: 60%; }
.attachment-image.ssn_card { max-width: 3.25in; max-height: 1.35in; width: auto; height: auto; }
`;

function attachmentImageClass(type) {
  const map = {
    proof_of_address: 'proof_of_address',
    government_id: 'government_id',
    ssn_card: 'ssn_card',
    data_breach_proof: 'data_breach_proof',
  };
  return map[type] || type.replace(/-/g, '_');
}

function toPrintableAttachment(att, overrides = {}) {
  const type = normalizeAttachmentType({ ...att, ...overrides });
  const merged = {
    ...att,
    type,
    category: type,
    label: getAttachmentDisplayLabel({ ...att, type }),
    preview_url: getAttachmentDisplayUrl(att),
    cropped_url: getAttachmentDisplayUrl(att),
    file_url: getAttachmentDisplayUrl(att),
    attached: att.attached !== false,
    ...overrides,
  };
  return {
    ...merged,
    ...buildPrintableRenderMetadata(type, merged),
  };
}

/**
 * Build ordered printable attachments for one letter/envelope.
 */
const STANDARD_ATTACHMENT_TYPES = new Set([
  'proof_of_address',
  'government_id',
  'ssn_card',
  'ssn_support',
  'data_breach_proof',
  'identity_theft_report',
]);

export function buildEnvelopePrintableAttachments({
  letter,
  packageIdentitySupport = {},
  dataBreachProof = null,
  perLetterAttachments = {},
  packageSharedAttachments = [],
  letterId,
  ssnApprovals = {},
}) {
  const attachments = [];
  const lid =
    letterId ||
    letter?.letter_id ||
    letter?.id ||
    `letter_${letter?.bureau?.toLowerCase() || 'unknown'}`;

  if (packageIdentitySupport?.proof_of_address) {
    attachments.push(
      toPrintableAttachment(packageIdentitySupport.proof_of_address, {
        id: packageIdentitySupport.proof_of_address.document_id || 'poa',
        type: 'proof_of_address',
        source: 'package_shared',
        ...PACKAGE_WIDE_ATTACHMENT_SCOPE,
      })
    );
  }

  if (packageIdentitySupport?.government_id) {
    attachments.push(
      toPrintableAttachment(packageIdentitySupport.government_id, {
        id: packageIdentitySupport.government_id.document_id || 'gov_id',
        type: 'government_id',
        source: 'package_shared',
        ...PACKAGE_WIDE_ATTACHMENT_SCOPE,
      })
    );
  }

  const ssnApproved = isSsnApprovedPackageWide(ssnApprovals);
  const ssnDoc =
    packageIdentitySupport?.ssn_card || packageIdentitySupport?.ssn_support;
  if (ssnApproved && ssnDoc) {
    attachments.push(
      toPrintableAttachment(ssnDoc, {
        id: ssnDoc.document_id || 'ssn_card',
        type: 'ssn_card',
        source: 'package_shared',
        ...PACKAGE_WIDE_ATTACHMENT_SCOPE,
      })
    );
  }

  if (isDataBreachLetter(letter)) {
    const effectiveProof = resolveEffectiveDataBreachProof(dataBreachProof);
    attachments.push(
      toPrintableAttachment(effectiveProof, {
        id: effectiveProof.document_id || 'system_data_breach_proof',
        type: 'data_breach_proof',
        source: effectiveProof.source || 'backend_auto',
      })
    );

    const letterSpecificEarly = perLetterAttachments[lid] || [];
    const identityTheftReport = letterSpecificEarly.find(
      (att) => normalizeAttachmentType(att) === 'identity_theft_report'
    );
    if (identityTheftReport) {
      attachments.push(
        toPrintableAttachment(identityTheftReport, {
          id: identityTheftReport.document_id || 'identity_theft_report',
          type: 'identity_theft_report',
          source: identityTheftReport.source || 'letter_specific',
          shared: false,
          apply_to_all_envelopes: false,
          scope: 'letter',
        })
      );
    }
  }

  (packageSharedAttachments || []).forEach((att) => {
    if (att.attached === false) return;
    if (isIdentityTheftReportAttachment(att)) return;
    const type = normalizeAttachmentType(att);
    if (STANDARD_ATTACHMENT_TYPES.has(type)) return;
    attachments.push(
      toPrintableAttachment(att, {
        source: att.source || 'package_shared',
        ...PACKAGE_WIDE_ATTACHMENT_SCOPE,
      })
    );
  });

  const letterSpecific = perLetterAttachments[lid] || [];
  letterSpecific.forEach((att) => {
    if (att.attached === false) return;
    const type = normalizeAttachmentType(att);
    if (STANDARD_ATTACHMENT_TYPES.has(type)) {
      return;
    }
    attachments.push(
      toPrintableAttachment(att, {
        source: att.source || 'letter_specific',
      })
    );
  });

  const deduped = [];
  const seenKeys = new Set();
  const sorted = [...attachments].sort((a, b) => {
    const ai = ATTACHMENT_TYPE_ORDER.indexOf(a.type);
    const bi = ATTACHMENT_TYPE_ORDER.indexOf(b.type);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  sorted.forEach((att) => {
    const type = att.type || 'other';
    const dedupeKey = STANDARD_ATTACHMENT_TYPES.has(type)
      ? type
      : (att.document_id || att.id || `${type}-${att.title || att.filename || ''}`);
    if (seenKeys.has(dedupeKey)) return;
    if (!getAttachmentDisplayUrl(att)) return;
    seenKeys.add(dedupeKey);
    deduped.push(att);
  });

  return deduped;
}

/** Attachment payloads for Step 8 mail quote / send APIs (same shape as PostGrid PDF preview). */
export function buildMailEnvelopeAttachments({
  envelope,
  packageIdentitySupport = {},
  dataBreachProof = null,
  perLetterAttachments = {},
  packageSharedAttachments = [],
  ssnApprovals = {},
}) {
  const letterId =
    envelope?.letter_id ||
    envelope?.letters?.[0]?.id ||
    envelope?.letters?.[0]?.letter_id ||
    envelope?.envelope_id;

  const attachments = buildPdfApiAttachments({
    letter: envelope,
    packageIdentitySupport,
    dataBreachProof: isDataBreachEnvelope(envelope) ? dataBreachProof : null,
    perLetterAttachments,
    packageSharedAttachments,
    letterId,
    ssnApprovals,
  });

  return attachments.map((att) => ({
    ...att,
    page_count: 1,
    auto_attached:
      att.type === 'data_breach_proof' &&
      (att.source === 'system_default' || att.source === 'backend_auto'),
  }));
}

/** Compact max image height for proof packet items (~35% smaller when 4+ items). */
export function compactAttachmentMaxHeight(count) {
  if (count >= 4) return '1.35in';
  if (count === 3) return '1.65in';
  if (count === 2) return '2.0in';
  return '2.75in';
}

function sortAttachmentsByTypeOrder(attachments = []) {
  return [...attachments].sort((a, b) => {
    const ai = ATTACHMENT_TYPE_ORDER.indexOf(normalizeAttachmentType(a));
    const bi = ATTACHMENT_TYPE_ORDER.indexOf(normalizeAttachmentType(b));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

function buildCompactProofPageHtml(attachments) {
  const sorted = sortAttachmentsByTypeOrder(attachments);
  const maxHeight = compactAttachmentMaxHeight(sorted.length);
  const blocks = sorted
    .map((att) => {
      const src = getAttachmentDisplayUrl(att);
      if (!src) return '';
      const label = getAttachmentDisplayLabel(att);
      const cls = attachmentImageClass(normalizeAttachmentType(att));
      return `
        <div class="attachment-item compact-proof-item">
          <p class="attachment-label">${label}</p>
          <img src="${src}" alt="${label}" class="attachment-image ${cls}" style="max-height: ${maxHeight}; max-width: 100%; object-fit: contain;" />
        </div>`;
    })
    .join('');

  if (!blocks) return '';

  return `<section class="appended-attachments-page compact-proof-page">
        <h2 class="appended-attachments-title">APPENDED ATTACHMENTS</h2>
        ${blocks}
      </section>`;
}

function buildFullPageAttachmentHtml(att) {
  const src = getAttachmentDisplayUrl(att);
  if (!src) return '';
  const type = normalizeAttachmentType(att);
  const alt = type === 'identity_theft_report'
    ? 'Identity Theft Report'
    : getAttachmentDisplayLabel(att);
  return `<section class="attachment-page-full">
        <img src="${src}" alt="${alt}" class="attachment-image-full" />
      </section>`;
}

function buildGroupedAttachmentsSectionHtml(attachments) {
  const sorted = sortAttachmentsByTypeOrder(attachments);
  const blocks = sorted
    .map((att) => {
      const src = getAttachmentDisplayUrl(att);
      if (!src) return '';
      const label = getAttachmentDisplayLabel(att);
      const cls = attachmentImageClass(normalizeAttachmentType(att));
      return `
        <div class="attachment-item">
          <p class="attachment-label">${label}</p>
          <img src="${src}" alt="${label}" class="attachment-image ${cls}" style="max-width: 100%; object-fit: contain;" />
        </div>`;
    })
    .join('');

  if (!blocks) return '';

  return `<section class="appended-attachments-page">
        <h2 class="appended-attachments-title">APPENDED ATTACHMENTS</h2>
        ${blocks}
      </section>`;
}

/** Build appended attachments HTML for iframe preview / print. */
export function buildAppendedAttachmentsHtml(attachments) {
  if (!attachments?.length) return '';

  const withUrls = (attachments || []).filter((att) => getAttachmentDisplayUrl(att));
  const compactProof = withUrls.filter(
    (att) => resolveAttachmentRenderMode(att) === RENDER_MODE_COMPACT_PROOF
  );
  const fullPage = withUrls.filter((att) => {
    const mode = resolveAttachmentRenderMode(att);
    return mode === RENDER_MODE_FULL_PAGE;
  });
  const grouped = withUrls.filter(
    (att) => resolveAttachmentRenderMode(att) === RENDER_MODE_IMAGE_GROUPED
  );

  const parts = [];
  const compactHtml = buildCompactProofPageHtml(compactProof);
  if (compactHtml) parts.push(compactHtml);
  fullPage.forEach((att) => {
    const block = buildFullPageAttachmentHtml(att);
    if (block) parts.push(block);
  });
  const groupedHtml = buildGroupedAttachmentsSectionHtml(grouped);
  if (groupedHtml) parts.push(groupedHtml);

  return parts.join('\n');
}
